import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  EMPTY,
  catchError,
  map,
  mergeMap,
  of,
  startWith,
  switchMap,
  take,
  withLatestFrom
} from 'rxjs';
import { AuthActions } from '../../../components/auth/auth.actions';
import { ReportViewActions } from './view.actions';
import {
  selectReportViewCategoryItems,
  selectReportViewReportType
} from './view.selectors';
import { ReportViewService } from './view.service';

/**
 * @description Report View Effects.
 * @exports
 * @class ReportViewEffects
 */
@Injectable()
export class ReportViewEffects extends ReportViewService {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => ReportViewActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onCheckEmptyCategories = createEffect(() => {
    return this.actions.pipe(
      ofType(ReportViewActions.checkEmptyCategories),
      switchMap(() =>
        this.store.select(selectReportViewCategoryItems).pipe(
          take(1),
          switchMap((categories) => {
            if (!categories || categories.length === 0) {
              return of(ReportViewActions.fetchCategory());
            }
            return EMPTY;
          })
        )
      )
    );
  });

  readonly onFetchCategory = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ReportViewActions.fetchCategory),
        withLatestFrom(this.store.select(selectReportViewReportType)),
        mergeMap((args) => {
          const reportType = args[1];
          return this.findAllCategory({
            sort: {
              name: 'ASC' as any
            },
            where: {
              reportType: reportType as any
            }
          }).pipe(
            mergeMap((data) =>
              of(
                ReportViewActions.setCategory({
                  count: data.count,
                  results: data.results
                }),
                ReportViewActions.setCategoryLoading({ loading: false })
              )
            ),
            catchError((error) => {
              // eslint-disable-next-line no-console
              console.error('onFetchCategory', error);
              return of(
                ReportViewActions.setCategory({
                  count: 0,
                  results: []
                }),
                ReportViewActions.setCategoryLoading({ loading: false })
              );
            }),
            startWith(ReportViewActions.setCategoryLoading({ loading: true }))
          );
        })
      );
    },
    { dispatch: true }
  );

  readonly onFetchReportType = createEffect(
    () => {
      return this.actions.pipe(
        ofType(ReportViewActions.fetchReportType),
        mergeMap(({ where }) => {
          return this.findReportTypeByCode({ where, limit: 1 }).pipe(
            mergeMap(({ results = [] }) =>
              of(
                ReportViewActions.setReportTypeDetail({
                  reportType: results[0] || null
                }),
                ReportViewActions.setReportTypeLoading({
                  loading: false
                })
              )
            ),
            catchError((error) => {
              // eslint-disable-next-line no-console
              console.error('onFetchReportType', error);
              return of(
                ReportViewActions.setReportTypeDetail({
                  reportType: null
                }),
                ReportViewActions.setReportTypeLoading({
                  loading: false
                })
              );
            }),
            startWith(
              ReportViewActions.setReportTypeLoading({
                loading: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );
}
